import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { getTrailerField } from '@src/common/utils/form/ValueGetters';
import type { Order } from '@src/orders/domain/Order';
import type { PermitApplication } from '@src/orders/domain/PermitApplication';
import { isEntryValueEmpty } from '@src/orders/domain/PermitApplication';
import {
  TractorActualWeightField,
  TractorGroundHeightField,
  TractorGVWField,
  TractorHeightField,
  TractorLegalWeight,
  TractorLengthField,
  TractorOuterBridgeLengthField,
  TractorUnladenWeightField,
  TractorWheelBaseField,
  TractorWidthField,
} from '@src/permitField/TractorDimensionsFields';
import {
  TractorAxleCountField,
  TractorBodyStyleField,
  TractorFloridaVehicleConfigurationField,
  TractorFuelTypeField,
  TractorIdField,
  TractorLicensePlateNumberField,
  TractorLicensePlateStateField,
  TractorMakeField,
  TractorModelNumberField,
  TractorOntarioVehicleTypeField,
  TractorTypeField,
  TractorUnitNumberField,
  TractorVINField,
  TractorYearField,
  VehicleOfSpecialDesignField,
} from '@src/permitField/TractorInformationFields';
import {
  TractorLicenseEffectiveDateField,
  TractorLicenseExpirationDateField,
  TractorLicenseIsApportionedPlateField,
  TractorLicensePlateTypeField,
  TractorLicensePlateYearField,
} from '@src/permitField/TractorLicenseFields';
import {
  OwnerAddressField,
  OwnerCityField,
  OwnerCompanyNameField,
  OwnerEmailField,
  OwnerPhoneField,
  OwnerStateField,
  OwnerZipCodeField,
  TractorIsLeasedField,
  TractorOwnerNameField,
  USDOTNumberOfOwningCompanyField,
} from '@src/permitField/TractorOwnerFields';
import {
  TrailerDeckHeightField,
  TrailerEmptyWeightField,
  TrailerGroundClearanceField,
  TrailerHeightField,
  TrailerKingpinSetbackLengthField,
  TrailerKingpinSettingLengthField,
  TrailerLengthField,
  TrailerRegisteredWeightField,
  TrailerWheelbaseLengthField,
  TrailerWidthField,
} from '@src/permitField/TrailerDimensionFields';
import {
  IsDualLaneField,
  IsTrunnionAxleField,
  TrailerAxleCountField,
  TrailerLicensePlateField,
  TrailerLicensePlateStateField,
  TrailerLicensePlateTypeField,
  TrailerMakeField,
  TrailerTypeField,
  TrailerUnitNumberField,
  TrailerVINField,
  TrailerYearField,
} from '@src/permitField/TrailerInformationFields';
import { CategorySection, FieldValue } from '@src/permits/CategorySection';
import React from 'react';
import { useTranslation } from 'react-i18next';

export const HaulingEquipmentCategory = ({
  permitApplication,
  order,
}: {
  permitApplication: PermitApplication;
  order: Order;
}) => {
  const { t } = useTranslation();
  const fields = permitApplication.getFieldsBySection('haulingEquipment');
  if (fields.size === 0) return null;

  const trailers = permitApplication.getFieldValue('trailers');
  const trailerCount = Array.isArray(trailers) ? trailers.length : 0;

  const requiredFields = Array.from(fields.values()).flat();

  const TrailerGeneralSection = ({
    idx,
    requiredFields,
    permitApplication,
  }: {
    idx: number;
    requiredFields: string[];
    permitApplication: PermitApplication;
  }) => {
    const trailerGeneral = [
      TrailerUnitNumberField.fieldName,
      TrailerMakeField.fieldName,
      TrailerTypeField.fieldName,
      TrailerVINField.fieldName,
      TrailerYearField.fieldName,
      TrailerAxleCountField.fieldName,
    ];

    const hasContent = trailerGeneral.some((field) => {
      const trailerGeneralValue = permitApplication.getTrailerField(field, idx);
      return !isEntryValueEmpty(trailerGeneralValue);
    });

    if (!hasContent) return null;

    return (
      <>
        <Stack>
          <Typography variant="body1">
            {t('trailer')} {idx + 1}
          </Typography>
        </Stack>
        {trailerGeneral.map((field) => (
          <FieldValue
            key={field}
            field={field}
            requiredFields={requiredFields}
            permitApplication={permitApplication}
            order={order}
            valueGetter={(_givenField: string, form: Record<string, unknown>) =>
              getTrailerField(field, idx, form)
            }
          />
        ))}
      </>
    );
  };

  const TrailerLicenseSection = ({
    idx,
    requiredFields,
    permitApplication,
  }: {
    idx: number;
    requiredFields: string[];
    permitApplication: PermitApplication;
  }) => {
    const trailerLicense = [
      TrailerLicensePlateField.fieldName,
      TrailerLicensePlateStateField.fieldName,
      TrailerLicensePlateTypeField.fieldName,
    ];

    const hasContent = trailerLicense.some((field) => {
      const trailerGeneralValue = permitApplication.getTrailerField(field, idx);
      return !isEntryValueEmpty(trailerGeneralValue);
    });

    if (!hasContent) return null;

    return (
      <>
        <Typography variant="body1">
          {t('trailerLicensePlateSection', { number: idx + 1 })}
        </Typography>
        {trailerLicense.map((field) => (
          <FieldValue
            key={field}
            field={field}
            requiredFields={requiredFields}
            permitApplication={permitApplication}
            order={order}
            valueGetter={() => permitApplication.getTrailerField(field, idx)}
          />
        ))}
      </>
    );
  };

  const TrailerDimensionsSection = ({
    idx,
    requiredFields,
    permitApplication,
  }: {
    idx: number;
    requiredFields: string[];
    permitApplication: PermitApplication;
  }) => {
    const trailerDimension = [
      TrailerLengthField.fieldName,
      TrailerWidthField.fieldName,
      TrailerHeightField.fieldName,
      TrailerWheelbaseLengthField.fieldName,
      TrailerDeckHeightField.fieldName,
      TrailerGroundClearanceField.fieldName,
      TrailerKingpinSetbackLengthField.fieldName,
      TrailerKingpinSettingLengthField.fieldName,
      TrailerEmptyWeightField.fieldName,
      TrailerRegisteredWeightField.fieldName,
    ];

    const hasContent = trailerDimension.some(
      (field) =>
        !isEntryValueEmpty(permitApplication.getTrailerField(field, idx)),
    );

    if (!hasContent) return null;

    return (
      <>
        <Typography variant="body1">
          {t('trailerDimensionsSection', { number: idx + 1 })}
        </Typography>
        {trailerDimension.map((field) => (
          <FieldValue
            key={field}
            field={field}
            requiredFields={requiredFields}
            permitApplication={permitApplication}
            order={order}
            valueGetter={() => permitApplication.getTrailerField(field, idx)}
          />
        ))}
      </>
    );
  };

  return (
    <CategorySection
      title={t('orderSections.haulingEquipment')}
      requiredFields={requiredFields}
      permitApplication={permitApplication}
      order={order}
      subsections={[
        {
          label: t('tractorInformation'),
          fields: [
            TractorIdField.fieldName,
            TractorUnitNumberField.fieldName,
            TractorMakeField.fieldName,
            TractorVINField.fieldName,
            TractorYearField.fieldName,
            TractorAxleCountField.fieldName,
            TractorLicensePlateNumberField.fieldName,
            TractorLicensePlateStateField.fieldName,
            TractorModelNumberField.fieldName,
            TractorTypeField.fieldName,
            TractorFuelTypeField.fieldName,
            TractorBodyStyleField.fieldName,
            TractorFloridaVehicleConfigurationField.fieldName,
            TractorOntarioVehicleTypeField.fieldName,
            VehicleOfSpecialDesignField.fieldName,
          ],
        },
        {
          label: t('tractorLicense'),
          fields: [
            TractorLicenseEffectiveDateField.fieldName,
            TractorLicenseExpirationDateField.fieldName,
            TractorLicensePlateTypeField.fieldName,
            TractorLicensePlateYearField.fieldName,
            TractorLicenseIsApportionedPlateField.fieldName,
          ],
        },
        {
          label: t('tractorDimensions'),
          fields: [
            TractorLegalWeight.fieldName,
            TractorLengthField.fieldName,
            TractorWidthField.fieldName,
            TractorHeightField.fieldName,
            TractorWheelBaseField.fieldName,
            TractorGroundHeightField.fieldName,
            TractorUnladenWeightField.fieldName,
            TractorActualWeightField.fieldName,
            TractorGVWField.fieldName,
            TractorOuterBridgeLengthField.fieldName,
          ],
        },
        {
          label: t('tractorOwner'),
          fields: [
            TractorIsLeasedField.fieldName,
            TractorOwnerNameField.fieldName,
            OwnerCompanyNameField.fieldName,
            USDOTNumberOfOwningCompanyField.fieldName,
            OwnerAddressField.fieldName,
            OwnerCityField.fieldName,
            OwnerStateField.fieldName,
            OwnerZipCodeField.fieldName,
            OwnerPhoneField.fieldName,
            OwnerEmailField.fieldName,
          ],
        },
        {
          label: t('trailerInformation'),
          renderFields: () => (
            <>
              <FieldValue
                field={IsDualLaneField.fieldName}
                requiredFields={requiredFields}
                permitApplication={permitApplication}
                order={order}
              />
              <FieldValue
                field={IsTrunnionAxleField.fieldName}
                requiredFields={requiredFields}
                permitApplication={permitApplication}
                order={order}
              />
              {Array.from({ length: trailerCount }).map((_, idx) => (
                <React.Fragment key={idx}>
                  <TrailerGeneralSection
                    idx={idx}
                    requiredFields={requiredFields}
                    permitApplication={permitApplication}
                  />
                  <TrailerLicenseSection
                    idx={idx}
                    requiredFields={requiredFields}
                    permitApplication={permitApplication}
                  />
                  <TrailerDimensionsSection
                    idx={idx}
                    requiredFields={requiredFields}
                    permitApplication={permitApplication}
                  />
                </React.Fragment>
              ))}
            </>
          ),
        },
      ]}
    />
  );
};
